import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminBoard } from "@/contexts/AdminBoardContext";
import axios from "axios";
import {
  Building2,
  Search,
  Bot,
  TrendingUp,
  Shield,
  AlertTriangle,
  PauseCircle,
  PlayCircle,
  ChevronRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = process.env.REACT_APP_API_URL;

export default function AdminClients() {
  const navigate = useNavigate();
  const { user, enableSafeMode, commandCenterData } = useAdminBoard();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/clients`, {
        headers: { Authorization: `Bearer ${user?.token}` }
      });
      setClients(res.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
      // Use mock data if API fails
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  const agents = commandCenterData?.agents || [];

  const handleSuspendClient = async (clientId) => {
    try {
      await axios.put(
        `${API}/api/admin/clients/${clientId}/status`,
        { status: "suspended" },
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      toast.success("Client suspended");
      fetchClients();
    } catch (error) {
      toast.error("Failed to suspend client");
    }
  };

  const handleActivateClient = async (clientId) => {
    try {
      await axios.put(
        `${API}/api/admin/clients/${clientId}/status`,
        { status: "active" },
        { headers: { Authorization: `Bearer ${user?.token}` } }
      );
      toast.success("Client activated");
      fetchClients();
    } catch (error) {
      toast.error("Failed to activate client");
    }
  };

  const handleEnableSafeMode = async (clientId) => {
    try {
      await enableSafeMode(clientId, "Admin enabled safe mode");
      toast.success("Safe mode enabled - AI restricted");
      fetchClients();
    } catch (error) {
      toast.error("Failed to enable safe mode");
    }
  };

  const getClientAgents = (businessId) => {
    return agents.filter(a => a.business_id === businessId);
  };

  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    client.industry?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isSuperAdmin = user?.role === "gsk_super_admin";

  // RBAC: Only Super Admin can access this page
  if (!isSuperAdmin) {
    return (
      <div className="p-6 bg-slate-50 min-h-full flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 mx-auto mb-4 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-500 text-sm">
              Only GSK Super Admins can access client management.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 bg-slate-50 min-h-full flex items-center justify-center">
        <div className="text-slate-500">Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6 bg-slate-50 min-h-full" data-testid="admin-clients">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Clients</h1>
          <p className="text-slate-500 text-sm mt-1">
            Manage all client businesses on the platform
          </p>
        </div>
        
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search clients..."
            className="pl-10 w-full lg:w-64 bg-white border-slate-300"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="client-search"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-cyan-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clients.length}</p>
                <p className="text-xs text-slate-500">Total Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <Bot className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{agents.length}</p>
                <p className="text-xs text-slate-500">Total Agents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-600">
                  {clients.filter(c => c.status === "active").length}
                </p>
                <p className="text-xs text-slate-500">Active Clients</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">
                  {clients.filter(c => c.status === "suspended").length}
                </p>
                <p className="text-xs text-slate-500">Suspended</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients List */}
      <Card className="bg-white border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">All Clients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredClients.map((client) => {
              const clientAgents = getClientAgents(client.id);
              const activeAgents = clientAgents.filter(a => a.is_active && !a.is_paused);
              
              return (
                <div
                  key={client.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors cursor-pointer"
                  onClick={() => navigate(`/admin/clients/${client.id}`)}
                  data-testid={`client-${client.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 rounded-lg bg-slate-200 flex items-center justify-center text-lg font-bold text-slate-600">
                      {client.name?.charAt(0) || "C"}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-900">{client.name}</p>
                        <Badge
                          variant="outline"
                          className={
                            client.status === "active"
                              ? "text-emerald-600 border-emerald-300 bg-emerald-50"
                              : "text-red-600 border-red-300 bg-red-50"
                          }
                        >
                          {client.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-500 capitalize">
                        {client.industry} • {clientAgents.length} agents ({activeAgents.length} active)
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    {client.status === "active" ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-400 text-amber-600 hover:bg-amber-50"
                          onClick={() => handleEnableSafeMode(client.id)}
                          data-testid={`safe-mode-${client.id}`}
                        >
                          <Shield className="w-4 h-4 mr-1" />
                          Safe Mode
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-400 text-red-600 hover:bg-red-50"
                          onClick={() => handleSuspendClient(client.id)}
                          data-testid={`suspend-${client.id}`}
                        >
                          <PauseCircle className="w-4 h-4 mr-1" />
                          Suspend
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-400 text-emerald-600 hover:bg-emerald-50"
                        onClick={() => handleActivateClient(client.id)}
                        data-testid={`activate-${client.id}`}
                      >
                        <PlayCircle className="w-4 h-4 mr-1" />
                        Activate
                      </Button>
                    )}
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                </div>
              );
            })}

            {filteredClients.length === 0 && (
              <div className="text-center py-12 text-slate-400">
                <Building2 className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="text-sm">No clients found</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
